<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { staticPageHref, accountHref, productHref, HOME_PRODUCTS, type ProductKey } from '@/router/products'
import { allowsRepeatSubscription, subscriptionFlowFor } from '@/subscriptionFlows'
import { useAuth } from '@/composables/useAuth'
import { useContracts } from '@/composables/useContracts'
import AccountLayout from '@/components/layout/AccountLayout.vue'
import Picto from '@/components/ui/Picto.vue'
import ContractTerminateAction from '@/components/ui/ContractTerminateAction.vue'
import ContractCertificateAction from '@/components/ui/ContractCertificateAction.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { member } = useAuth()
const {
  subscribedProductKeys,
  contractForProduct,
  contractCountForProduct,
  certificateContractForProduct,
  reload,
} = useContracts()

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))
const contactHref = computed(() => accountHref('contact', currentLocale.value))
const cardHref = computed(() => accountHref('card', currentLocale.value))
const contractsHref = computed(() => accountHref('contracts', currentLocale.value))
useHead({ title: computed(() => `${t('auth.account.subscribe.navTitle')} — MySmerra`) })

/** Accès réservé. */
onMounted(() => {
  if (!member.value) router.replace(loginHref.value)
})

/** Un produit propose la « carte de tiers payant » quand son parcours collecte
 *  un RIB (FlowFieldRequirements.banking) — c.-à-d. les produits santé avec
 *  tiers payant. Source unique : la définition de flow, pas une liste en dur. */
function hasTpCard(key: ProductKey): boolean {
  return subscriptionFlowFor(key)?.fields.banking === true
}

/** Pictos par produit, identiques à la home (src/assets/icons/, voir ChoicePanel). */
const productIcons: Record<ProductKey, string> = {
  healthExpenses: 'Picto_depense_sante_orange',
  housing: 'Picto_smerra-logement-etudiant_orange',
  civil: 'Picto_assurance_RC_orange',
  abroad: 'Picto_smerra_voyage_orange',
  arrivingFrance: 'valises',
  healthTopUp: 'Picto_engagements-etudiants_orange',
  regionalAura: 'Picto_depense_sante_orange',
  solidariteExam: 'Picto_engagements-etudiants_orange',
}

/** Lien de souscription : interne (parcours) ou externe (devis hors site). */
const products = computed(() =>
  HOME_PRODUCTS.map((p) => {
    // Certificate source: the product's own contract, or — for a cross-sell
    // product — the main contract that covers it (see useContracts).
    const certificateContract = certificateContractForProduct(p.key)
    return {
      key: p.key,
      label: t('subscription.products.items.' + p.key),
      href: p.externalHref ?? productHref(p.key, currentLocale.value),
      external: !!p.externalHref,
      subscribed: subscribedProductKeys.value.has(p.key),
      // SI-1477 — holding a stay-abroad cover does not use up the right to take out another:
      // a second trip, or an extension of the current one, is a second contract. The card
      // stays the management card — the running contract still needs its certificate and its
      // termination — and gains a way back into the journey.
      repeatable: allowsRepeatSubscription(p.key),
      // Actions that name one contract — the certificate to download, the cover to retract —
      // stop making sense once the member holds several for the same product: the card would
      // be acting on whichever came back first. They are dropped rather than guessed. "Active"
      // here ignores dates: a cover starting next month is one the member holds.
      ambiguous: contractCountForProduct(p.key) > 1,
      health: hasTpCard(p.key),
      contract: contractForProduct(p.key),
      certificateContract,
      // « Voir les détails » d'un produit déjà souscrit : la page « Mes contrats »
      // avec le contrat concerné présélectionné via ?ref=<référence>.
      detailsHref: certificateContract
        ? `${contractsHref.value}?ref=${encodeURIComponent(certificateContract.reference)}`
        : null,
    }
  }),
)

</script>

<template>
  <AccountLayout v-if="member" active="subscribe">
    <section class="sub">
      <!-- Fil d'ariane + titre -->
      <nav class="sub__crumb" aria-label="breadcrumb">
        <span>{{ t('auth.account.subscribe.breadcrumbHome') }}</span>
        <span class="sub__crumb-sep" aria-hidden="true">›</span>
        <span class="sub__crumb-current">{{ t('auth.account.subscribe.navTitle') }}</span>
      </nav>
      <header class="sub__head">
        <h1 class="sub__title">{{ t('auth.account.subscribe.navTitle') }}</h1>
        <p class="sub__subtitle">{{ t('auth.account.subscribe.subtitle') }}</p>
      </header>

      <div class="sub__grid">
        <template v-for="p in products" :key="p.key">
          <!-- Produit déjà souscrit : souscription impossible, actions de gestion. -->
          <div
            v-if="p.subscribed"
            class="pcard pcard--subscribed"
            :class="{ 'pcard--settled': !p.repeatable }"
          >
            <div class="pcard__head">
              <span class="pcard__icon" :class="{ 'pcard__icon--ok': !p.repeatable }">
                <Picto :name="productIcons[p.key]" :size="30" />
              </span>
              <span class="pcard__check" aria-hidden="true">
                <svg viewBox="0 0 20 20" width="20" height="20"><rect x="2" y="2" width="16" height="16" rx="4" fill="currentColor"/><path d="M6 10.5l2.5 2.5L14 7" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
            </div>
            <span class="pcard__label">{{ p.label }}</span>
            <span class="pcard__status">
              <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true"><path d="M5 10.5l3 3 7-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              {{ t('auth.account.subscribe.subscribedLabel') }}
            </span>
            <div class="pcard__actions">
              <ContractCertificateAction
                v-if="p.certificateContract && !p.ambiguous"
                :contract="p.certificateContract"
                variant="pill"
              />
              <RouterLink v-if="p.health" class="pcard__act" :to="cardHref">{{ t('auth.account.subscribe.tpCardCta') }}</RouterLink>
              <ContractTerminateAction
                v-if="p.contract && !p.ambiguous"
                :contract="p.contract"
                variant="pill"
                @terminated="reload"
              />
            </div>
            <RouterLink v-if="p.repeatable" class="pcard__cta pcard__again" :to="p.href">
              {{ t('auth.account.subscribe.repeatCta') }}
              <svg class="pcard__arrow" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M4 10h11M10 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </RouterLink>
            <RouterLink v-if="p.detailsHref" class="pcard__cta pcard__details" :to="p.detailsHref">
              {{ t('auth.account.subscribe.detailsCta') }}
              <svg class="pcard__arrow" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M4 10h11M10 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </RouterLink>
          </div>

          <!-- Produit souscriptible : carte cliquable vers le parcours. -->
          <component
            v-else
            :is="p.external ? 'a' : 'RouterLink'"
            class="pcard"
            :href="p.external ? p.href : undefined"
            :to="p.external ? undefined : p.href"
            :target="p.external ? '_blank' : undefined"
            :rel="p.external ? 'noopener' : undefined"
          >
            <span class="pcard__icon">
              <Picto :name="productIcons[p.key]" :size="30" />
            </span>
            <span class="pcard__label">{{ p.label }}</span>
            <span class="pcard__cta">
              {{ p.external ? t('auth.account.subscribe.external') : t('auth.account.subscribe.cta') }}
              <svg v-if="p.external" class="pcard__ext" viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                <path d="M8 5H5v10h10v-3M11 4h5v5M16 4l-7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg v-else class="pcard__arrow" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M4 10h11M10 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </component>
        </template>
      </div>

      <!-- Aide au choix -->
      <article class="help">
        <span class="help__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
            <path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1.9-1.1 1.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            <circle cx="12" cy="16.5" r="1" fill="currentColor"/>
          </svg>
        </span>
        <div class="help__body">
          <h2 class="help__title">{{ t('auth.account.subscribe.helpTitle') }}</h2>
          <p class="help__desc">{{ t('auth.account.subscribe.helpDesc') }}</p>
        </div>
        <RouterLink class="btn-dark help__cta" :to="contactHref">{{ t('auth.account.subscribe.helpCta') }}</RouterLink>
      </article>
    </section>
  </AccountLayout>
</template>

<style scoped>
.sub { display: flex; flex-direction: column; gap: 1rem; }

/* Fil d'ariane + titre */
.sub__crumb { display: flex; align-items: center; gap: 0.4rem; font-size: var(--text-sm); color: var(--text-muted); }
.sub__crumb-current { color: var(--color-chocolate); font-weight: 600; }
.sub__title { font-size: var(--text-xl); font-weight: 700; color: var(--color-chocolate); }
.sub__subtitle { margin-top: 0.15rem; font-size: var(--text-sm); color: var(--text-muted); }

/* Grille catalogue */
.sub__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
  gap: 1rem;
  margin-top: 0.25rem;
}

/* Carte produit */
.pcard {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 1.1rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  text-decoration: none;
  transition: border-color var(--dur-fast), box-shadow var(--dur-fast), transform var(--dur-base) var(--ease-spring);
}
.pcard:hover { border-color: var(--primary); box-shadow: var(--shadow-md); transform: translateY(-2px); }
.pcard__icon {
  display: grid;
  place-items: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: var(--radius-lg);
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 12%, white);
}
.pcard__label { flex: 1; font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); line-height: var(--leading-snug); }
.pcard__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
}
.pcard:hover .pcard__cta { color: var(--color-cherry); }

/* « Voir les détails » d'un produit déjà souscrit : même style que le CTA
   « Souscrire », mais lien autonome (la carte souscrite n'est pas cliquable). */
.pcard__details { align-self: flex-start; text-decoration: none; }
.pcard__details:hover { color: var(--color-cherry); }

/* « Souscrire à nouveau » (SI-1477). Même famille que les deux CTA ci-dessus, en plus
   appuyé : sur une carte qui annonce « Déjà souscrit », c'est l'action que l'adhérent ne
   s'attend pas à trouver, et la rendre discrète reviendrait à la cacher une seconde fois.
   Posé avant « Voir les détails » dans le DOM, donc lu en premier au clavier comme au
   lecteur d'écran. */
.pcard__again {
  align-self: flex-start;
  text-decoration: none;
  font-weight: 700;
}
.pcard__again:hover { color: var(--color-cherry); }

/* Carte produit déjà souscrit (vert, non cliquable) */
.pcard--subscribed {
  cursor: default;
}
.pcard--subscribed:hover { transform: none; box-shadow: var(--shadow-sm); }

/* Le vert dit « c'est réglé, rien à faire de plus ». Il ne vaut donc que pour les produits
   qui se tiennent à un contrat à la fois : là où réadhérer reste possible, il contredirait la
   CTA juste en dessous. Carte et picto reprennent alors l'orange d'une vignette souscriptible
   — c'est bien ce qu'elle redevient.
   Seule la coche reste verte : elle dit que l'adhérent est couvert, ce qui est vrai dans les
   deux cas. C'est la fermeture de la vignette qui change, pas la couverture.
   Piloté par `repeatable`, donc un autre produit ouvert à la réadhésion suit sans rien à
   ajouter ici (SI-1477). */
.pcard--settled {
  border-color: color-mix(in oklab, #1a7f45 35%, white);
  background: color-mix(in oklab, #1a7f45 7%, white);
}
.pcard--settled:hover { border-color: color-mix(in oklab, #1a7f45 35%, white); }
.pcard__head { display: flex; align-items: flex-start; justify-content: space-between; }
.pcard__icon--ok { color: #1a7f45; background: color-mix(in oklab, #1a7f45 12%, white); }
.pcard__check { color: #1a7f45; line-height: 0; }
.pcard__status {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: var(--text-xs);
  font-weight: 700;
  color: #1a7f45;
}
.pcard__actions { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.2rem; }
.pcard__act {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.7rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  background: var(--color-white);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-chocolate);
  text-decoration: none;
  cursor: pointer;
  transition: border-color var(--dur-fast), color var(--dur-fast);
}
.pcard__act:hover { border-color: var(--primary); color: var(--primary); }
.pcard__act--danger { color: var(--color-cherry); border-color: color-mix(in oklab, var(--color-cherry) 35%, white); }
.pcard__act--danger:hover { border-color: var(--color-cherry); color: var(--color-cherry); }

/* Aide au choix */
.help {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  column-gap: 0.9rem;
  row-gap: 0.6rem;
  padding: 1.1rem;
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius-lg);
  background: var(--bg-soft);
}
.help__icon {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  border-radius: var(--radius-lg);
  color: var(--text-muted);
  background: var(--color-white);
}
.help__title { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.help__desc { margin-top: 0.15rem; font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }
.help__cta { text-decoration: none; }
@media (max-width: 40rem) {
  .help { grid-template-columns: auto 1fr; }
  .help__cta { grid-column: 1 / -1; justify-self: start; }
}

/* Bouton sombre */
.btn-dark {
  padding: 0.7rem 1.4rem;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--color-chocolate);
  color: var(--color-white);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-out);
}
.btn-dark:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }
</style>
